// Faces from Google Fonts' css2 API, as live loads a text's fonts (the editor's texts, the layer
// editor's text layers): a stylesheet once per page, then the faces a text's characters need. The
// app's own type (Inter, Space Grotesk) is self-hosted in fonts.ts and never taken from here.

/** One style of a family: its weight and slant. */
export interface GoogleStyle {
  weight: number;
  italic: boolean;
}

/** A face a text needs: its family, weight and slant, and the characters it draws. */
export interface GoogleFace extends GoogleStyle {
  family: string;
  text: string;
}

/** A family's stylesheet with these styles, upright then slanted, light to heavy, `display=swap`. */
export function googleStylesheet(family: string, styles: readonly GoogleStyle[]): string {
  const axis = [...styles]
    .sort((one, other) => Number(one.italic) - Number(other.italic) || one.weight - other.weight)
    .map((style) => `${style.italic ? 1 : 0},${style.weight}`)
    .join(";");
  const name = encodeURIComponent(family).replaceAll("%20", "+");
  return `https://fonts.googleapis.com/css2?family=${name}:ital,wght@${axis}&display=swap`;
}

/** The CSS font a face is asked for by. */
function fontOf(face: GoogleFace): string {
  return `${face.italic ? "italic " : ""}${face.weight} 16px "${face.family}"`;
}

// By stylesheet: two callers may ask for one family with different styles.
const sheets = new Map<string, Promise<void>>();
const sheetsIn = new Set<string>();

/** Adds a stylesheet to the page once; resolves when it has loaded or failed. */
function sheetOf(stylesheet: string): Promise<void> {
  let pending = sheets.get(stylesheet);
  if (pending === undefined) {
    pending = new Promise<void>((resolve) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = stylesheet;
      link.crossOrigin = "anonymous";
      link.addEventListener("load", () => resolve());
      link.addEventListener("error", () => resolve());
      document.head.append(link);
    }).then(() => {
      sheetsIn.add(stylesheet);
    });
    sheets.set(stylesheet, pending);
  }
  return pending;
}

/** Whether the page can wait for fonts at all (a test's page cannot: its text is never drawn, and
 * its `document.fonts` is a stand-in that loads nothing). */
function fontsAvailable(): boolean {
  return typeof document !== "undefined" && "fonts" in document && typeof document.fonts.load === "function";
}

/**
 * Whether a face is ready for its text: its stylesheet is in and every face its characters need
 * is loaded (a text drawn before would show another face, so it waits).
 */
export function googleFaceReady(face: GoogleFace, stylesheet: string): boolean {
  if (!fontsAvailable()) return true;
  return sheetsIn.has(stylesheet) && document.fonts.check(fontOf(face), face.text || " ");
}

/**
 * Loads a face for its text: its stylesheet, then the faces those characters need. A face that
 * cannot load (offline, blocked) resolves anyway, and its text draws in the browser's fallback.
 */
export async function loadGoogleFace(face: GoogleFace, stylesheet: string): Promise<void> {
  if (!fontsAvailable()) return;
  await sheetOf(stylesheet);
  await document.fonts.load(fontOf(face), face.text || " ").catch(() => undefined);
}
